import React from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Divider,
    Button,
    Stack,
} from "@mui/material";
import { Description, Link, Phone, Message, AccessTime, Done, DoneAll } from "@mui/icons-material";
import { WB_UI } from "../../../ui/ux/uiTokens";

const TemplateMessageBubble = ({
    templateDetails,
    payload,
    mediaLink,
    mediaType,
    sender = "bot", // "bot" for outgoing, "user" for incoming
    timestamp,
    status,
    isBotReply = false,
    senderName = null,
}) => {
    if (!templateDetails || !templateDetails.components) {
        return null;
    }

    const headerComponent = templateDetails.components.find(
        (c) => c.type === "HEADER"
    );
    const bodyComponent = templateDetails.components.find(
        (c) => c.type === "BODY"
    );
    const footerComponent = templateDetails.components.find(
        (c) => c.type === "FOOTER"
    );
    const buttonsComponent = templateDetails.components.find(
        (c) => c.type === "BUTTONS"
    );

    // Extract parameters from payload components
    const extractParameters = (components) => {
        if (!components || !Array.isArray(components)) return { header: [], body: [] };
        
        const headerParams = [];
        const bodyParams = [];
        
        components.forEach((component) => {
            if (component.type === "HEADER" && component.parameters) {
                component.parameters.forEach((param) => {
                    if (param.type === "text") {
                        headerParams.push(param.text);
                    }
                });
            }
            if (component.type === "BODY" && component.parameters) {
                component.parameters.forEach((param) => {
                    if (param.type === "text") {
                        bodyParams.push(param.text);
                    }
                });
            }
        });
        
        return { header: headerParams, body: bodyParams };
    };

    // Helper function to get components from payload (handles both structures)
    const getComponents = () => {
        // Try direct components first (normalized structure)
        if (payload?.components && Array.isArray(payload.components)) {
            return payload.components;
        }
        // Fallback to template.components (WhatsApp API format)
        if (payload?.template?.components && Array.isArray(payload.template.components)) {
            return payload.template.components;
        }
        return [];
    };

    const parameters = extractParameters(getComponents());

    // Build preview text with parameters
    const replaceParameters = (text, paramArray) => {
        if (!text || !paramArray || paramArray.length === 0) return text || "";
        let result = text;
        paramArray.forEach((param, index) => {
            result = result.replace(
                new RegExp(`\\{\\{${index + 1}\\}\\}`, "g"),
                param || `{{${index + 1}}}`
            );
        });
        return result;
    };

    const headerText =
        headerComponent?.format === "TEXT"
            ? replaceParameters(headerComponent.text, parameters?.header)
            : null;

    const bodyText = bodyComponent
        ? replaceParameters(bodyComponent.text, parameters?.body)
        : null;

    // Build media URL from media_link
    const getMediaUrl = () => {
        if (!mediaLink) return null;
        // If media_link is already a full URL, use it as is
        if (mediaLink.startsWith("http://") || mediaLink.startsWith("https://")) {
            return mediaLink;
        }
        // Otherwise, construct URL from cloudflare_path
        // Remove leading slash if present to avoid double slashes
        const cleanPath = mediaLink.startsWith("/") ? mediaLink.slice(1) : mediaLink;
        // Construct Cloudflare R2 URL (adjust base URL if needed)
        return `https://pub-b8f74165803246fbaee6ed60fe08c081.r2.dev/${cleanPath}`;
    };

    const mediaUrl = getMediaUrl();

    // Determine bubble styling based on sender
    const isOutgoing = sender === "bot";
    const bubbleStyle = {
        maxWidth: "75%",
        backgroundColor: isOutgoing
            ? (isBotReply ? "#6B46C1" : "#e6eef5")
            : "#ffffff",
        borderRadius: isOutgoing ? "7.5px 0 7.5px 7.5px" : "0 7.5px 7.5px 7.5px",
        boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
        border: "1px solid #e5e5e5",
        ml: isOutgoing ? "auto" : 0,
        mr: isOutgoing ? 0 : "auto",
        ...(isOutgoing && isBotReply ? { color: "rgba(255,255,255,0.95)" } : {}),
    };

    return (
        <Card sx={bubbleStyle}>
            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 2 } }}>
                {/* Header (Text) */}
                {headerComponent?.format === "TEXT" && headerText && (
                    <>
                        <Typography
                            variant="body2"
                            fontWeight={WB_UI.typography.bodyStrong.fontWeight}
                            sx={{
                                mb: 1,
                                color: isOutgoing && isBotReply ? "rgba(255,255,255,0.95)" : "#075e54",
                            }}
                        >
                            {headerText}
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                    </>
                )}

                {/* Header (Media) */}
                {headerComponent &&
                    ["IMAGE", "VIDEO", "DOCUMENT"].includes(
                        headerComponent.format
                    ) && (
                        <Box sx={{ mb: 1 }}>
                            {mediaUrl ? (
                                <Box>
                                    {headerComponent.format === "IMAGE" && (
                                        <Box
                                            component="img"
                                            src={mediaUrl}
                                            alt="Media"
                                            onError={(e) => {
                                                console.error("Image load error:", e);
                                                e.target.style.display = "none";
                                            }}
                                            sx={{
                                                width: "100%",
                                                maxHeight: 200,
                                                borderRadius: 1,
                                                display: "block",
                                                objectFit: "contain",
                                            }}
                                        />
                                    )}
                                    {headerComponent.format === "VIDEO" && (
                                        <Box
                                            component="video"
                                            src={mediaUrl}
                                            controls
                                            sx={{
                                                width: "100%",
                                                maxHeight: 200,
                                                borderRadius: 1,
                                                display: "block",
                                            }}
                                        />
                                    )}
                                    {headerComponent.format === "DOCUMENT" && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                p: 1.5,
                                                backgroundColor: "#f5f5f5",
                                                borderRadius: 1,
                                            }}
                                        >
                                            <Description fontSize="large" color="primary" />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={
                                                        WB_UI.typography.bodyStrong.fontWeight
                                                    }
                                                >
                                                    Document
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {mediaLink.split("/").pop() || "Document"}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                <Typography variant="caption" color="text.secondary">
                                    Media not available
                                </Typography>
                            )}
                        </Box>
                    )}

                {/* Body */}
                {bodyText && (
                    <Typography
                        variant="body2"
                        sx={{
                            lineHeight: 1.5,
                            whiteSpace: "pre-wrap",
                            mb: footerComponent?.text || buttonsComponent?.buttons ? 1 : 0,
                        }}
                    >
                        {bodyText}
                    </Typography>
                )}

                {/* Footer */}
                {footerComponent?.text && (
                    <>
                        {(bodyText || headerComponent) && <Divider sx={{ my: 1, borderColor: isOutgoing && isBotReply ? "rgba(255,255,255,0.4)" : undefined }} />}
                        <Typography
                            variant="caption"
                            sx={{
                                color: isOutgoing && isBotReply ? "rgba(255,255,255,0.9)" : "text.secondary",
                                fontSize: "0.75rem",
                            }}
                        >
                            {footerComponent.text}
                        </Typography>
                    </>
                )}

                {/* Buttons */}
                {buttonsComponent?.buttons && buttonsComponent.buttons.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                        <Divider sx={{ mb: 1.5 }} />
                        <Stack spacing={1}>
                            {buttonsComponent.buttons.map((button, index) => {
                                const getButtonIcon = () => {
                                    const buttonType = button.type || "QUICK_REPLY";
                                    switch (buttonType) {
                                        case "URL":
                                            return <Link fontSize="small" />;
                                        case "PHONE_NUMBER":
                                            return <Phone fontSize="small" />;
                                        case "QUICK_REPLY":
                                        default:
                                            return <Message fontSize="small" />;
                                    }
                                };

                                return (
                                    <Button
                                        key={index}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        startIcon={getButtonIcon()}
                                        sx={{
                                            borderColor: "#25d366",
                                            color: "#25d366",
                                            textTransform: "none",
                                            fontSize: "0.75rem",
                                            justifyContent: "center",
                                            "&:hover": {
                                                borderColor: "#128c7e",
                                                backgroundColor: "rgba(37, 211, 102, 0.1)",
                                            },
                                        }}
                                    >
                                        {button.text || button.type}
                                    </Button>
                                );
                            })}
                        </Stack>
                    </Box>
                )}
            </CardContent>

            {/* Message Time and Status */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: isOutgoing ? "flex-end" : "flex-start",
                    px: 2,
                    pb: 1.5,
                    gap: 0.25,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            color: isOutgoing && isBotReply ? "rgba(255,255,255,0.9)" : "text.secondary",
                            fontSize: "0.7rem",
                        }}
                    >
                        {timestamp || new Date().toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                    </Typography>
                {isOutgoing && status && (
                    <>
                        {status.toUpperCase() === "PENDING" && (
                            <AccessTime
                                sx={{
                                    fontSize: 12,
                                    opacity: 0.7,
                                    color: isBotReply ? "rgba(255,255,255,0.9)" : "text.secondary",
                                }}
                            />
                        )}
                        {status.toUpperCase() === "SENT" && (
                            <Done
                                sx={{
                                    fontSize: 12,
                                    opacity: 0.7,
                                    color: isBotReply ? "rgba(255,255,255,0.9)" : "text.secondary",
                                }}
                            />
                        )}
                        {status.toUpperCase() === "DELIVERED" && (
                            <DoneAll
                                sx={{
                                    fontSize: 12,
                                    opacity: 0.7,
                                    color: isBotReply ? "rgba(255,255,255,0.9)" : "text.secondary",
                                }}
                            />
                        )}
                        {status.toUpperCase() === "READ" && (
                            <DoneAll
                                sx={{
                                    fontSize: 12,
                                    opacity: 1,
                                    color: "#4fc3f7", // Blue color like WhatsApp
                                }}
                            />
                        )}
                        {status.toUpperCase() === "FAILED" && (
                            <Box
                                component="span"
                                sx={{
                                    fontSize: 12,
                                    opacity: 0.7,
                                    color: "error.main",
                                }}
                            >
                                ⚠
                            </Box>
                        )}
                    </>
                )}
                </Box>
                {isOutgoing && senderName && (
                    <Typography
                        variant="caption"
                        sx={{
                            color: isBotReply ? "rgba(255,255,255,0.9)" : "text.secondary",
                            fontSize: "0.7rem",
                        }}
                    >
                        {senderName}
                    </Typography>
                )}
            </Box>
        </Card>
    );
};

export default TemplateMessageBubble;

